import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { getAnnouncementsList, getUnreadCount } from "./reducer";
import { AnnouncementList } from "Shared/Types/fleetmanagementapi";

interface AnnouncementState {
  announcementList: {
    results: AnnouncementList[];
    count: number;
    loading: boolean;
    error?: string | null;
    isSuccess: boolean;
    hasMore: boolean;
  };
  unreadCount: number;
  unreadSuccess: boolean;
}

const initialState: AnnouncementState = {
  announcementList: {
    loading: false,
    error: null,
    hasMore: false,
    results: [],
    count: 0,
    isSuccess: false,
  },
  unreadCount: 0,
  unreadSuccess: false,
};

const AnnouncementsSlice = createSlice({
  name: "announcements",
  initialState,
  reducers: {
    clear(state) {
      state.announcementList.error = null;
      state.announcementList.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAnnouncementsList.fulfilled, (state, action) => {
      if (action.meta.arg.params && action.meta.arg.params.offset === 0) {
        state.announcementList.results = [];
      }

      state.announcementList.loading = false;
      state.announcementList.error = null;

      const existingIds = new Set(
        state.announcementList.results.map((a) => a.id),
      );
      const newResults = action.payload.results.filter(
        (a: AnnouncementList) => !existingIds.has(a.id),
      );

      state.announcementList.results = [
        ...state.announcementList.results,
        ...newResults,
      ];
      state.announcementList.hasMore = !!action.payload.next;
      state.announcementList.count = action.payload.count;
      state.announcementList.isSuccess = true;
    });
    builder.addCase(getAnnouncementsList.pending, (state) => {
      state.announcementList.loading = true;
      state.announcementList.error = null;
      state.announcementList.isSuccess = false;
      state.announcementList.hasMore = false;
    });
    builder.addCase(getAnnouncementsList.rejected, (state, action) => {
      state.announcementList.loading = false;
      state.announcementList.error = action.error.message;
      state.announcementList.isSuccess = false;
      state.announcementList.hasMore = false;
    });
    builder.addCase(getUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload.count;
      state.unreadSuccess = true;
    });
    builder.addCase(getUnreadCount.rejected, (state, action) => {
      state.unreadCount = 0;
      state.unreadSuccess = false;
    });
    builder.addCase(getUnreadCount.pending, (state) => {
      state.unreadCount = 0;
      state.unreadSuccess = false;
    });
  },
});

export const { clear } = AnnouncementsSlice.actions;

export const selectAnnouncementsList = (state: RootState) =>
  state.announcements.announcementList.results;
export const selectAnnouncementsListCount = (state: RootState) =>
  state.announcements.announcementList.count;
export const selectAnnouncementsListLoading = (state: RootState) =>
  state.announcements.announcementList.loading;
export const selectAnnouncementsListSuccess = (state: RootState) =>
  state.announcements.announcementList.isSuccess;
export const selectAnnouncementsListHasMore = (state: RootState) =>
  state.announcements.announcementList.hasMore;
export default AnnouncementsSlice;
export const selectUnreadCount = (state: RootState) =>
  state.announcements.unreadCount;
export const selectUnreadSuccess = (state: RootState) =>
  state.announcements.unreadSuccess;
